import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../assets/contexts/notificationContext';
import '../css/global.css';
import { buttonGlobalStyles } from '../theme/global';
import { UserContext } from '../assets/contexts/userContext';
import Loader from '../components/loader';

function LoginModal({ open = false, onClose = () => {} }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { changeUser, login } = useContext(UserContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const u = await login(email, password);
      changeUser?.(u);
      onClose();

      if (u.role === 'admin') navigate('/admin/adminDashboard');
      else if (u.role === 'employee') navigate('/employee/employeeDashboard');
      else navigate('/');
    } catch {
      showNotification('Usuario o contraseña incorrectos', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Helmet>
        <title>Acceso | CES</title>
        <meta name="description" content="Inicia sesión en la plataforma." />
      </Helmet>

      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown' || reason === 'backdropClick') onClose();
        }}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0,0,0,0.3)',
              pointerEvents: 'auto',
            },
          },
          paper: {
            sx: {
              background: 'rgba(255, 255, 255, 0.86)',
              backdropFilter: 'blur(40px)',
              p: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontFamily: 'Nunito, Raleway, sans-serif',
            fontWeight: 'bold',
            p: 4,
          }}
        >
          Acceso a CES
        </DialogTitle>

        <DialogContent
          component="form"
          onSubmit={handleLogin}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            p: '3em',
          }}
        >
          <TextField
            sx={{
              mt: 1,
              cursor: 'pointer',
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: 'var(--color-26)' },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--color-26)',
                '&.Mui-focused': { color: 'var(--color-26)' },
              },
            }}
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            sx={{
              mt: 1,
              cursor: 'pointer',
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: 'var(--color-26)' },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--color-26)',
                '&.Mui-focused': { color: 'var(--color-26)' },
              },
            }}
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin(e)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button type="submit" variant="contained" fullWidth sx={buttonGlobalStyles} onClick={handleLogin} >
            Acceder
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LoginModal;
